"use client";

import NumberFlow from "@number-flow/react";
import { memo, useCallback, useContext } from "react";
import { LuThumbsDown, LuThumbsUp } from "react-icons/lu";

import { InfoTooltip } from "@/components/shared/info-tooltip";
import { TriggerLoggedInModalWindow } from "@/components/shared/is-logged-in";
import { Button } from "@/components/ui/button";
import { useFocusMarketData } from "@/contexts/focus-market-data";
import { usePostContext } from "@/contexts/post-context";
import { UserContext } from "@/contexts/user-context";
import { useReactionLogic } from "@/hooks/use-reaction-logic";
import { AssociationReactionValue } from "@/utils/constants";
import { cn } from "@/utils/lib";

interface PostUpvoteProps {
  compact?: boolean;
  horizontal?: boolean;
}

export const PostUpvote = memo(
  ({ compact = false, horizontal = false }: PostUpvoteProps) => {
    const { user } = useContext(UserContext);
    const { loading: marketDataLoading } = useFocusMarketData();
    const { post, repost, isPureRepost } = usePostContext();

    const postHash = isPureRepost ? repost?.postHash : post?.postHash;

    if (!postHash) {
      throw new Error("Post hash is required");
    }

    const { handleReaction, reactions, cappedWealthUsdCents } =
      useReactionLogic({
        postHash,
      });

    const USDWealth = cappedWealthUsdCents! / 100;

    const isUpvoted = reactions[AssociationReactionValue.LIKE].isReacted;
    const isDownvoted = reactions[AssociationReactionValue.DISLIKE].isReacted;
    const actionButtonDisabled = !user?.publicKey || marketDataLoading;

    const onUpvoteClick = useCallback(() => {
      handleReaction({ associationValue: AssociationReactionValue.LIKE });
    }, [handleReaction]);

    const onDownvoteClick = useCallback(() => {
      handleReaction({ associationValue: AssociationReactionValue.DISLIKE });
    }, [handleReaction]);

    // We remove cents on amount > 10 to save space
    const wealth = USDWealth > 10 ? Math.floor(USDWealth) : USDWealth;

    const renderPostUpvoteContent = () => {
      return (
        <div
          className={cn("flex min-w-[30px] items-center justify-center gap-0", {
            "min-h-[64px] flex-col": !horizontal,
            "flex-row": horizontal,
          })}
        >
          <Button
            size="icon"
            variant="ghost"
            className={cn(
              `
                h-auto w-[24px] p-0 text-lg
                hover:-rotate-12
              `,
              {
                "text-primary opacity-100": isUpvoted,
                "opacity-50 hover:text-primary": !isUpvoted,
              },
            )}
            disabled={actionButtonDisabled}
            onClick={onUpvoteClick}
          >
            <LuThumbsUp />
          </Button>
          <div className="flex items-center justify-center">
            <InfoTooltip
              text="Sum of all voters net worth. Capped at $1k per user."
              side="right"
              className="max-w-[200px] text-center"
              delayDuration={0}
            >
              <div
                className={cn(
                  "my-1 font-mono text-xs text-foreground lowercase",
                  {
                    "md:text-xs": compact,
                    "md:text-sm": !compact,
                    "text-red-500": USDWealth < 0,
                  },
                )}
              >
                {USDWealth !== 0 ? (
                  <NumberFlow
                    value={wealth}
                    trend={USDWealth > 0 ? 1 : USDWealth < 0 ? -1 : 0}
                    format={{
                      style: "currency",
                      currency: "USD",
                      notation: "compact",
                      currencyDisplay: "narrowSymbol",
                      maximumFractionDigits:
                        USDWealth > 10 || USDWealth < -10 ? 0 : 2,
                    }}
                  />
                ) : (
                  <span className="text-muted" />
                )}
              </div>
            </InfoTooltip>
          </div>
          <Button
            size="icon"
            variant="ghost"
            className={cn(
              `
                h-auto w-[24px] p-0 text-lg
                hover:-rotate-12
              `,
              {
                "text-primary opacity-100": isDownvoted,
                "opacity-50 hover:text-primary": !isDownvoted,
              },
            )}
            disabled={actionButtonDisabled}
            onClick={onDownvoteClick}
          >
            <LuThumbsDown />
          </Button>
        </div>
      );
    };

    return (
      <TriggerLoggedInModalWindow>
        {renderPostUpvoteContent()}
      </TriggerLoggedInModalWindow>
    );
  },
);

PostUpvote.displayName = "PostUpvote";
